"use client"

import { useState, useEffect } from "react"
import "./App.css"

function App() {
  const [markdown, setMarkdown] = useState(
    '# Hello, Markdown!\n\nStart typing your markdown here...\n\n## Features\n\n- Edit markdown\n- Preview in real-time\n- Export to PDF\n\n```js\nconsole.log("Hello, world!");\n```\n\n## Table Example\n\n| Header 1 | Header 2 | Header 3 |\n| -------- | -------- | -------- |\n| Cell 1   | Cell 2   | Cell 3   |\n| Cell 4   | Cell 5   | Cell 6   |',
  )
  const [activeTab, setActiveTab] = useState("edit")
  const [renderedHtml, setRenderedHtml] = useState("")

  // Process markdown and update rendered HTML
  useEffect(() => {
    setRenderedHtml(renderMarkdown(markdown))
  }, [markdown])

  // Completely rewritten markdown renderer with robust table support
  function renderMarkdown(text) {
    // First, split the text into blocks to handle different elements
    const blocks = text.split(/\n\n+/)
    let html = ""

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i].trim()

      // Skip empty blocks
      if (!block) continue

      // Check if this block contains a table
      if (block.includes("|") && block.includes("\n")) {
        const tableResult = parseTable(block)
        if (tableResult) {
          html += tableResult
          continue
        }
      }

      // Check if this is a code block
      if (block.startsWith("```") && block.endsWith("```")) {
        const code = block.substring(3, block.length - 3).trim()
        html += `<pre><code>${escapeHtml(code)}</code></pre>`
        continue
      }

      // Check if this is a header
      if (block.startsWith("# ")) {
        html += `<h1>${processInlineElements(block.substring(2))}</h1>`
        continue
      }
      if (block.startsWith("## ")) {
        html += `<h2>${processInlineElements(block.substring(3))}</h2>`
        continue
      }
      if (block.startsWith("### ")) {
        html += `<h3>${processInlineElements(block.substring(4))}</h3>`
        continue
      }

      // Check if this is a list
      if (block.match(/^[*-] /m)) {
        const listItems = block
          .split(/\n/)
          .map((line) => {
            if (line.startsWith("* ") || line.startsWith("- ")) {
              return `<li>${processInlineElements(line.substring(2))}</li>`
            }
            return ""
          })
          .join("")
        html += `<ul>${listItems}</ul>`
        continue
      }

      // Default to paragraph
      html += `<p>${processInlineElements(block)}</p>`
    }

    return html
  }

  // Specialized function to parse tables
  function parseTable(text) {
    const lines = text.split("\n").filter((line) => line.trim())

    // Need at least 3 lines for a table (header, separator, data)
    if (lines.length < 3) return null

    // Check if the second line is a separator line
    const separatorLine = lines[1].trim()
    if (!separatorLine.includes("|") || !separatorLine.includes("-")) return null

    // Check if all separator cells contain only dashes, colons, and spaces
    const separatorCells = separatorLine.split("|").map((cell) => cell.trim())
    for (let i = 0; i < separatorCells.length; i++) {
      const cell = separatorCells[i]
      if (cell && !/^[-:\s]+$/.test(cell)) return null
    }

    // At this point, we're confident this is a table
    let tableHtml = '<table class="markdown-table">'

    // Process header row
    const headerCells = lines[0]
      .split("|")
      .map((cell) => cell.trim())
      .filter((cell) => cell)
    tableHtml += "<thead><tr>"
    for (const cell of headerCells) {
      tableHtml += `<th>${processInlineElements(cell)}</th>`
    }
    tableHtml += "</tr></thead>"

    // Process data rows
    tableHtml += "<tbody>"
    for (let i = 2; i < lines.length; i++) {
      const rowCells = lines[i]
        .split("|")
        .map((cell) => cell.trim())
        .filter((cell) => cell)
      tableHtml += "<tr>"
      for (const cell of rowCells) {
        tableHtml += `<td>${processInlineElements(cell)}</td>`
      }
      tableHtml += "</tr>"
    }
    tableHtml += "</tbody></table>"

    return tableHtml
  }

  // Helper function to process inline markdown elements
  function processInlineElements(text) {
    // Handle inline code
    text = text.replace(/`([^`]+)`/g, (_, code) => `<code>${escapeHtml(code)}</code>`)

    // Handle bold
    text = text.replace(/\*\*([^*]+)\*\*/g, (_, content) => `<strong>${content}</strong>`)

    // Handle italic
    text = text.replace(/\*([^*]+)\*/g, (_, content) => `<em>${content}</em>`)

    return text
  }

  // Helper function to escape HTML
  function escapeHtml(text) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }

  const handleExportPDF = () => {
    // Create a new window
    const printWindow = window.open("", "_blank")

    // Write the HTML content
    printWindow.document.write(`
      <html>
        <head>
          <title>Markdown Document</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            pre {
              background-color: #f6f8fa;
              border-radius: 3px;
              padding: 16px;
              overflow: auto;
            }
            code {
              font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
              background-color: rgba(27, 31, 35, 0.05);
              border-radius: 3px;
              padding: 0.2em 0.4em;
              font-size: 85%;
            }
            pre code {
              background-color: transparent;
              padding: 0;
            }
            h1, h2, h3 {
              margin-top: 24px;
              margin-bottom: 16px;
              font-weight: 600;
              line-height: 1.25;
            }
            h1 {
              font-size: 2em;
              border-bottom: 1px solid #eaecef;
              padding-bottom: 0.3em;
            }
            h2 {
              font-size: 1.5em;
              border-bottom: 1px solid #eaecef;
              padding-bottom: 0.3em;
            }
            h3 {
              font-size: 1.25em;
            }
            ul, ol {
              padding-left: 2em;
            }
            li {
              margin-top: 0.25em;
            }
            /* Explicit table styling for PDF */
            table.markdown-table {
              border-collapse: collapse;
              width: 100%;
              margin: 16px 0;
              border: 1px solid #dfe2e5;
              table-layout: fixed;
            }
            table.markdown-table th,
            table.markdown-table td {
              border: 1px solid #dfe2e5;
              padding: 8px 12px;
              text-align: left;
              word-wrap: break-word;
            }
            table.markdown-table th {
              background-color: #f6f8fa;
              font-weight: 600;
            }
            table.markdown-table tr:nth-child(even) {
              background-color: #f8f8f8;
            }
            @media print {
              body {
                padding: 0;
                margin: 0;
              }
              pre, code {
                white-space: pre-wrap;
              }
              table.markdown-table {
                page-break-inside: avoid;
                display: table;
                width: 100%;
              }
            }
          </style>
        </head>
        <body>
          <div class="markdown-content">
            ${renderedHtml}
          </div>
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `)

    printWindow.document.close()
  }

  return (
    <div className="container">
      <header className="header">
        <h1>Markdown Editor</h1>
        <div className="header-actions">
          <p className="subtitle">Edit markdown, preview, and export to PDF</p>
          <button className="export-button" onClick={handleExportPDF}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export PDF
          </button>
        </div>
      </header>

      <div className="editor-container">
        {/* Mobile view with tabs */}
        <div className="mobile-view">
          <div className="tabs">
            <button className={`tab ${activeTab === "edit" ? "active" : ""}`} onClick={() => setActiveTab("edit")}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              Edit
            </button>
            <button
              className={`tab ${activeTab === "preview" ? "active" : ""}`}
              onClick={() => setActiveTab("preview")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              Preview
            </button>
          </div>
          {activeTab === "edit" ? (
            <div className="tab-content">
              <textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                className="editor-textarea"
                placeholder="Type your markdown here..."
              />
            </div>
          ) : (
            <div className="tab-content preview-panel">
              <div className="preview-content">
                <div className="markdown-body" dangerouslySetInnerHTML={{ __html: renderedHtml }} />
              </div>
            </div>
          )}
        </div>

        {/* Desktop view with split screen */}
        <div className="desktop-view">
          <div className="panel editor-panel">
            <div className="panel-header">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <h2>Edit</h2>
            </div>
            <textarea
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              className="editor-textarea"
              placeholder="Type your markdown here..."
            />
          </div>

          <div className="panel preview-panel">
            <div className="panel-header">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              <h2>Preview</h2>
            </div>
            <div className="preview-content">
              <div className="markdown-body" dangerouslySetInnerHTML={{ __html: renderedHtml }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default App
